import React, { useState } from 'react';

export default function EnvioMasivo() {
  const [formData, setFormData] = useState({
    titulo: '',
    descripcion: '',
    link: '',
    imagen: null,
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState({ type: '', text: '' });

  const API_URL = process.env.REACT_APP_API_URL; // URL de tu API externa para enviar el mensaje masivo

  // Manejador genérico para inputs de texto
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Manejador específico para la imagen con generación de preview
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, imagen: file }));
      setImagePreview(URL.createObjectURL(file));
    } else {
      setFormData((prev) => ({ ...prev, imagen: null }));
      setImagePreview(null);
    }
  };

  // Envío de datos al backend
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatusMessage({ type: '', text: '' });

    // Instanciamos FormData porque estamos enviando un archivo
    const payload = new FormData();
    payload.append('titulo', formData.titulo);
    payload.append('descripcion', formData.descripcion);
    payload.append('link', formData.link);
    if (formData.imagen) {
      payload.append('imagen', formData.imagen);
    }

    try {
      // Reemplaza esta URL con el endpoint de tu API 
      const response = await fetch(`${API_URL}broadcast/masivo`, {
        method: 'POST',
        headers: {
          // Nota: Al usar FormData con fetch, NO establezcas el Content-Type. 
          // El navegador lo configura automáticamente con el boundary correcto.
          'Accept': 'application/json',
          // 'Authorization': `Bearer ${token}` // Descomentar si usas tokens
        },
        body: payload,
      });

      if (!response.ok) throw new Error('Error en la respuesta del servidor' + response.statusText);

      const data = await response.json();
      setStatusMessage({ type: 'success', text: `¡Mensaje enviado a ${data.conversations_count || 'las'} conversaciones activas!` });
      
      // Limpiar formulario tras éxito
      setFormData({ titulo: '', descripcion: '', link: '', imagen: null });
      setImagePreview(null);

    } catch (error) {
      setStatusMessage({ type: 'error', text: 'Ocurrió un error al procesar el envío masivo.' });
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="envio-masivo-page max-w-6xl mx-auto p-6 grid grid-cols-1 md:grid-cols-2 gap-8">
      {/* Columna Izquierda: Formulario */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Nueva Campaña</h2>
          <p className="text-gray-500 text-sm mt-1">
            Se enviará a todas las conversaciones con actividad (last_message_at) en las últimas 24 horas.
          </p>
        </div>

        {statusMessage.text && (
          <div className={`p-4 mb-4 rounded-md ${statusMessage.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
            {statusMessage.text}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Título</label>
            <input
              type="text"
              name="titulo"
              value={formData.titulo}
              onChange={handleChange}
              placeholder="Ej: ¡Nueva actualización disponible!"
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Descripción</label>
            <textarea
              name="descripcion"
              value={formData.descripcion}
              onChange={handleChange}
              rows="4"
              placeholder="Escribe el cuerpo del mensaje..."
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Enlace (Opcional)</label>
            <input
              type="url"
              name="link"
              value={formData.link}
              onChange={handleChange}
              placeholder="https://tudominio.com/promo"
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Imagen (Opcional)</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="w-full file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full py-3 rounded-lg font-bold text-white transition-colors ${
              isSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {isSubmitting ? 'Procesando Envíos...' : 'Enviar Masivo Ahora'}
          </button>
        </form>
      </div>

      {/* Columna Derecha: Vista Previa */}
      <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 flex flex-col items-center justify-center">
        <h3 className="text-lg font-semibold text-gray-600 mb-4 w-full text-center">Vista Previa del Mensaje</h3>
        
        {/* Simulador del ChatBubble */}
        <div className="bg-white max-w-sm w-full rounded-2xl shadow-md overflow-hidden border border-gray-100">
          {imagePreview && (
            <img src={imagePreview} alt="Preview" className="w-full h-auto object-cover" />
          )}
          <div className="p-4">
            <h4 className="font-bold text-gray-900 text-lg mb-1">
              {formData.titulo || 'Título del mensaje'}
            </h4>
            <p className="text-gray-700 text-sm whitespace-pre-wrap mb-3">
              {formData.descripcion || 'La descripción de tu post aparecerá aquí...'}
            </p>
            {formData.link && (
              <a href={formData.link} target="_blank" rel="noopener noreferrer" className="text-blue-500 text-sm hover:underline break-all">
                {formData.link}
              </a>
            )}
            <div className="text-right mt-2 text-xs text-gray-400">
              {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}